import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Check, Sparkles, X } from "lucide-react";
import { getEnglishBookName } from "@/pages/Home";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Plans() {
  const { lang } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [progress, setProgress] = useState({});
  const [selected, setSelected] = useState(null);
  const [autoOpen, setAutoOpen] = useState(null); // { planId, day } from ?open=&day=

  useEffect(() => {
    (async () => {
      const p = await api.get("/plans");
      setPlans(p.data);
      const prog = {};
      for (const pl of p.data) {
        try {
          const r = await api.get(`/plans/${pl.plan_id}/progress`);
          prog[pl.plan_id] = r.data.completed_days || [];
        } catch { prog[pl.plan_id] = []; }
      }
      setProgress(prog);

      // If we arrived from the daily reminder banner or a push notification with
      // ?open=<plan_id>&day=<n>, expand that plan and let PlanReadings auto-open the day.
      const openId = searchParams.get("open");
      const openDay = parseInt(searchParams.get("day") || "0", 10);
      if (openId && openDay > 0) {
        const found = p.data.find((pl) => pl.plan_id === openId);
        if (found) {
          setSelected(openId);
          setAutoOpen({ planId: openId, day: openDay });
          // Scroll into view once the DOM updates
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="plan-card-${openId}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 400);
        }
        // Clean the URL so refresh doesn't re-open the modal
        searchParams.delete("open");
        searchParams.delete("day");
        setSearchParams(searchParams, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)]">FaithCycle</p>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">
        {t("yourReadingPlans", lang)}
      </h1>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {plans.map((p) => {
          const pct = ((progress[p.plan_id]?.length || 0) / p.duration_days) * 100;
          const isOpen = selected === p.plan_id;
          return (
            <div
              key={p.plan_id}
              data-testid={`plan-card-${p.plan_id}`}
              className="rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-6"
            >
              <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)] flex items-center gap-2">
                <span>{p.duration_days} {t("days", lang)}</span>
                {p.is_devotional && (
                  <span className="flex items-center gap-1 text-[var(--accent)]">
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                    {lang === "es" ? "Devocional" : "Devotional"}
                  </span>
                )}
              </p>
              <h3 className="font-serif text-2xl mt-3 tracking-tight">
                {lang === "es" ? p.name_es : p.name_en}
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
                {lang === "es" ? p.description_es : p.description_en}
              </p>

              <div className="mt-5">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mb-2">
                  <span>{t("progress", lang)}</span>
                  <span>{progress[p.plan_id]?.length || 0} / {p.duration_days}</span>
                </div>
                <Progress value={pct} className="h-1.5 bg-[var(--surface)]" />
              </div>

              <button
                data-testid={`toggle-plan-${p.plan_id}`}
                onClick={() => setSelected(isOpen ? null : p.plan_id)}
                className="mt-5 w-full h-10 rounded-full bg-[var(--text-primary)] text-[var(--bg)] hover:bg-[var(--accent)] hover:text-white transition-colors duration-300 text-sm uppercase tracking-widest"
              >
                {isOpen ? (lang === "es" ? "Ocultar" : "Hide") : t("startPlan", lang)}
              </button>

              {isOpen && (
                <PlanReadings
                  plan={p}
                  completed={progress[p.plan_id] || []}
                  autoOpenDay={autoOpen?.planId === p.plan_id ? autoOpen.day : null}
                  onAutoOpenConsumed={() => setAutoOpen(null)}
                  onUpdate={(days) => setProgress((prev) => ({ ...prev, [p.plan_id]: days }))}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlanReadings({ plan, completed, autoOpenDay, onAutoOpenConsumed, onUpdate }) {
  const { lang } = useApp();
  const navigate = useNavigate();
  const [devotionalDay, setDevotionalDay] = useState(null);

  const toggle = async (day, isDone) => {
    const res = await api.post(`/plans/${plan.plan_id}/progress`, {
      day,
      completed: !isDone,
    });
    onUpdate(res.data.completed_days);
  };

  const openReading = (r) => {
    if (plan.is_devotional) {
      setDevotionalDay(r);
    } else {
      navigate(`/read?b=${r.book}&c=${r.chapter}&plan=${plan.plan_id}&day=${r.day}`);
    }
  };

  // Auto-open a specific day when coming from the daily reminder / push notification
  useEffect(() => {
    if (autoOpenDay && plan.is_devotional) {
      const r = plan.readings.find((rr) => rr.day === autoOpenDay);
      if (r) {
        setDevotionalDay(r);
        onAutoOpenConsumed?.();
      }
    } else if (autoOpenDay && !plan.is_devotional) {
      // For regular Bible plans opened via reminder, route to the Reader with plan context
      const r = plan.readings.find((rr) => rr.day === autoOpenDay);
      if (r) {
        navigate(`/read?b=${r.book}&c=${r.chapter}&plan=${plan.plan_id}&day=${r.day}`);
        onAutoOpenConsumed?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenDay, plan.plan_id]);

  return (
    <>
      <div className="mt-6 space-y-2 max-h-[400px] overflow-y-auto pr-2">
        {plan.readings.map((r) => {
          const isDone = completed.includes(r.day);
          return (
            <div
              key={r.day}
              data-testid={`plan-day-${plan.plan_id}-${r.day}`}
              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border transition-colors duration-200 ${
                isDone ? "border-[var(--accent)]/40 bg-[var(--highlight)]/30" : "border-[var(--border)]"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <button
                  data-testid={`plan-mark-${plan.plan_id}-${r.day}`}
                  onClick={() => toggle(r.day, isDone)}
                  className={`w-6 h-6 rounded-full border flex items-center justify-center transition-colors duration-200 flex-shrink-0 ${
                    isDone ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)] hover:border-[var(--accent)]"
                  }`}
                >
                  {isDone && <Check className="w-3.5 h-3.5" strokeWidth={2} />}
                </button>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)]">
                    {t("day", lang)} {r.day}
                  </p>
                  <p className="text-sm font-serif-body truncate">
                    {plan.is_devotional ? r.title : `${getEnglishBookName(r.book)} ${r.chapter}`}
                  </p>
                </div>
              </div>
              <button
                data-testid={`plan-read-${plan.plan_id}-${r.day}`}
                onClick={() => openReading(r)}
                className="text-xs uppercase tracking-widest text-[var(--accent)] hover:underline flex-shrink-0"
              >
                {plan.is_devotional ? (lang === "es" ? "Abrir" : "Open") : t("read", lang)}
              </button>
            </div>
          );
        })}
      </div>

      <DevotionalDialog
        open={!!devotionalDay}
        onClose={() => setDevotionalDay(null)}
        plan={plan}
        reading={devotionalDay}
        isDone={devotionalDay ? completed.includes(devotionalDay.day) : false}
        onComplete={async () => {
          if (!devotionalDay) return;
          const res = await api.post(`/plans/${plan.plan_id}/progress`, {
            day: devotionalDay.day,
            completed: true,
          });
          onUpdate(res.data.completed_days);
          toast.success(`Día ${devotionalDay.day} completado ✓`);
        }}
      />
    </>
  );
}

function DevotionalDialog({ open, onClose, plan, reading, isDone, onComplete }) {
  const { lang } = useApp();
  const navigate = useNavigate();
  if (!reading) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[var(--surface-elevated)] border-[var(--border)] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)]">
            {lang === "es" ? "Día" : "Day"} {reading.day} · {lang === "es" ? plan.name_es : plan.name_en}
          </p>
          <DialogTitle className="font-serif text-2xl md:text-3xl tracking-tight text-left">
            {reading.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          <button
            onClick={() => {
              if (reading.book && reading.chapter) {
                navigate(`/read?b=${reading.book}&c=${reading.chapter}${reading.verse ? `&v=${reading.verse}` : ""}`);
              }
            }}
            className="w-full text-left rounded-2xl border border-[var(--accent)]/30 bg-[var(--highlight)]/30 p-5 hover:border-[var(--accent)] transition-colors duration-200"
          >
            <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--accent)] mb-2">
              {reading.verse_ref}
            </p>
            <p className="font-serif-body text-lg italic leading-relaxed">
              "{reading.verse_text}"
            </p>
            <p className="text-[10px] uppercase tracking-widest text-[var(--text-secondary)] mt-2">
              {lang === "es" ? "Abrir en el lector" : "Open in reader"} →
            </p>
          </button>

          <Section title={lang === "es" ? "Comentario" : "Commentary"}>
            <p className="font-serif-body leading-relaxed whitespace-pre-line">{reading.commentary}</p>
          </Section>

          {reading.meditation && (
            <Section title={lang === "es" ? "Para meditar" : "For reflection"}>
              <p className="font-serif-body leading-relaxed italic">{reading.meditation}</p>
            </Section>
          )}

          {reading.action && (
            <Section title={lang === "es" ? "Acción de hoy" : "Today's action"}>
              <p className="font-serif-body leading-relaxed">{reading.action}</p>
            </Section>
          )}

          {reading.prayer && (
            <Section title={lang === "es" ? "Oración" : "Prayer"}>
              <p className="font-serif-body leading-relaxed">{reading.prayer}</p>
            </Section>
          )}

          <div className="flex gap-3 pt-2">
            <button
              data-testid={`devotional-complete-${plan.plan_id}-${reading.day}`}
              onClick={async () => {
                if (isDone) { onClose(); return; }
                await onComplete();
                onClose();
              }}
              className={`flex-1 h-12 rounded-full text-sm uppercase tracking-widest flex items-center justify-center gap-2 transition-colors duration-200 ${
                isDone
                  ? "bg-[var(--accent)] text-white"
                  : "bg-[var(--text-primary)] text-[var(--bg)] hover:bg-[var(--accent)] hover:text-white"
              }`}
            >
              <Check className="w-4 h-4" strokeWidth={2} />
              {isDone
                ? (lang === "es" ? "Completado" : "Completed")
                : (lang === "es" ? "Marcar como leído" : "Mark as read")}
            </button>
            <button
              onClick={onClose}
              className="h-12 px-6 rounded-full border border-[var(--border)] text-sm uppercase tracking-widest hover:border-[var(--accent)] transition-colors duration-200"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="uppercase tracking-[0.25em] text-[10px] text-[var(--text-secondary)] mb-2">{title}</p>
      {children}
    </div>
  );
}
